import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Combobox, ComboboxInput, ComboboxOptions } from "@headlessui/react";
import { cn } from "~/lib/utils";
import { useRouter } from "next/navigation";
import {
    RecentSearchItem,
    SearchResultItem,
    SearchWindowRow,
} from "../../types";
import { useMemo, useRef, useState } from "react";
import { useNavigatorPlatform } from "~/modules/app/hooks/useNavigatorPlatform";
import { HotkeyBadge } from "./HotkeyBadge";
import { EmptyState } from "./EmptyState";
import { LoadingState } from "./LoadingState";
import { toRecentSearchResultItem } from "../../util/recentSearches";
import {
    getSearchWindowRows,
    isSearchWindowHeaderRow,
} from "../../util/get-search-window-rows";
import { SearchWindowHeader } from "./SearchWindowHeader";
import { SearchWindowOption } from "./SearchWindowOption";

// // // //

// NOTE - these are stable references (instead of inline `[]` defaults)
// because a new array would rebuild + remount the virtualized rows on every render
const EMPTY_RESULTS: SearchResultItem[] = [];
const EMPTY_RECENT_SEARCHES: RecentSearchItem[] = [];

// // // //

/**
 * Renders the search input and results window for GlobalSearch and CommandPalette components
 * The results are virtualized via the Combobox `virtual` prop, which windows the rows with @tanstack/react-virtual
 */
export function SearchWindow(props: {
    setOpen: (bool: boolean) => void;
    autofocus?: boolean;
    groups: Record<string, SearchResultItem[]>;
    query: string;
    onOpen?: () => void;
    onClose: () => void;
    setQuery: (q: string) => void;
    items: SearchResultItem[];
    topResults?: SearchResultItem[];
    loadingSearch?: boolean;
    hideResults?: boolean;
    showInteractionTip?: boolean;
    onSelectResult: (result: SearchResultItem) => void;
    recentSearches?: RecentSearchItem[];
    onClearRecentSearches?: () => void;
    size?: "md" | "lg";
}) {
    const {
        query,
        setOpen,
        onOpen,
        onClose,
        setQuery,
        autofocus,
        items,
        groups,
        topResults = EMPTY_RESULTS,
        loadingSearch = false,
        hideResults = false,
        showInteractionTip = false,
        recentSearches = EMPTY_RECENT_SEARCHES,
        onClearRecentSearches,
        size = "lg",
        onSelectResult,
    } = props;

    const router = useRouter();
    const [focused, setFocused] = useState(false);
    const { isAppleOS } = useNavigatorPlatform();
    const inputRef = useRef<HTMLInputElement>(null);
    const optionsRef = useRef<HTMLElement>(null);
    const recentSearchResultItems = useMemo(
        () => recentSearches.map(toRecentSearchResultItem),
        [recentSearches],
    );

    // Define flag to show results when appropriate
    const canShowResults = !hideResults || focused;

    // Define flag to show results when appropriate
    const hasQuery = query.trim() !== "";

    // Define flag to show top results when appropriate
    const showTopResults = canShowResults && hasQuery && topResults.length > 0;

    // Define flag to show recent searches when appropriate
    const showRecentSearches =
        canShowResults && !hasQuery && recentSearchResultItems.length > 0;

    // Define the flat list of header + option rows to virtualize
    const rows = useMemo(
        () =>
            getSearchWindowRows({
                recentSearches: showRecentSearches
                    ? recentSearchResultItems
                    : EMPTY_RESULTS,
                topResults: showTopResults ? topResults : EMPTY_RESULTS,
                groups,
            }),
        [
            groups,
            recentSearchResultItems,
            showRecentSearches,
            showTopResults,
            topResults,
        ],
    );

    // Define the Combobox virtualization config
    const virtual = useMemo(
        () => ({ options: rows, disabled: isSearchWindowHeaderRow }),
        [rows],
    );

    // Define flag to show grouped results when appropriate
    const showGroupedResults = canShowResults && items.length > 0;

    // Define flag to show the search results content when appropriate
    const showWindowContent =
        showRecentSearches || showTopResults || showGroupedResults;

    // Define flag to show usage info when appropriate
    // const showUsageInfo = showWindowContent && query === "";

    // Define flag to show loading state when appropriate
    const showLoadingState = (!hideResults || focused) && loadingSearch;

    // Define flag to show empty state when appropriate
    const showEmptyState =
        hasQuery && canShowResults && items.length === 0 && !loadingSearch;

    return (
        <Combobox<SearchWindowRow>
            virtual={virtual}
            onChange={(row) => {
                // ENHANCEMENT - add support for "open in new tab"
                // if (openInNewTab) {
                //     window.open(item.href, "_blank");
                //     return;
                // }
                if (!row || row.type !== "option") {
                    return;
                }

                const { item } = row;
                router.push(item.href);
                onSelectResult(item);
                setOpen(false);

                // Blur the Combobox.Input element
                inputRef?.current?.blur();
            }}
        >
            <div className="relative">
                <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className={cn(
                        "pointer-events-none absolute left-4 text-muted-foreground",
                        {
                            "top-3 h-4 w-4": size === "md",
                            "top-3.5 h-5 w-5": size === "lg",
                        },
                    )}
                    aria-hidden="true"
                />
                <ComboboxInput
                    ref={inputRef}
                    autoFocus={autofocus}
                    placeholder="Search any asset, issuer, ticker, or entity..."
                    className={cn(
                        "w-full border-border border-0 bg-transparent pl-11 pr-4 text-base md:text-sm font-normal text-slate-900 placeholder:font-light placeholder:text-muted-foreground focus:ring-0",
                        {
                            "border-b!": focused,
                            "h-10": size === "md",
                            "h-12": size === "lg",
                        },
                    )}
                    onFocus={() => {
                        setFocused(true);
                        if (onOpen) {
                            onOpen();
                        }
                    }}
                    onBlur={() => {
                        // NOTE - we wrap this in a setTimeout
                        // because hiding the Combobox.Options below without a delay will remove thier UI
                        // before the user's click event can register on the element.
                        setTimeout(() => {
                            setFocused(false);
                            setOpen(false);
                            setQuery("");
                            onClose();
                        }, 100);
                    }}
                    onChange={(event) => {
                        setQuery(event.target.value);

                        // Scroll back to the top of the list for the new results
                        // NOTE - this is done here (instead of in an effect on the rows) because
                        // the virtualizer restores its last scroll offset while measuring new rows
                        if (optionsRef.current) {
                            optionsRef.current.scrollTop = 0;
                        }
                    }}
                />

                {/* Conditionally render HotkeyBadge */}
                {showInteractionTip && <HotkeyBadge isAppleOS={isAppleOS} />}
            </div>

            {/* Conditionally show loading state */}
            {showLoadingState && <LoadingState />}

            {/* Conditionally show empty state */}
            {showEmptyState && <EmptyState />}

            {showWindowContent && !showLoadingState && (
                <ComboboxOptions
                    ref={optionsRef}
                    as="div"
                    static
                    // NOTE - the virtualizer reads its scroll padding from this element's
                    // padding, so `pt-11` keeps the section header in view when keyboard
                    // navigation scrolls up. The negative margin on the virtualizer's
                    // wrapper (the only child) removes that padding from the layout.
                    className={cn(
                        "overflow-y-auto pt-11 pb-2 text-foreground [&>div]:-mt-11",
                        {
                            "max-h-96": size === "md",
                            "max-h-150": size === "lg",
                        },
                    )}
                >
                    {({ option: row }: { option: SearchWindowRow }) => {
                        if (row.type === "header") {
                            return (
                                <SearchWindowHeader
                                    row={row}
                                    onClearRecentSearches={
                                        onClearRecentSearches
                                    }
                                />
                            );
                        }

                        return <SearchWindowOption row={row} />;
                    }}
                </ComboboxOptions>
            )}
        </Combobox>
    );
}
